import { HELP_BASE, HELP_INDEX_FILE, HELP_LIMITS, HELP_MSG, HelpIndex, helpArticlesFor, helpSearch, type HelpEventKind, type HelpIndexArticle, type HelpShot } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { api } from "../api.js";
import { t } from "../labels.js";
import { currentProduct, productHeaders } from "../lib/product.js";
import { record } from "../lib/trail.js";
import { useImageViewer } from "./ImageViewer.jsx";
import { Field, guardComposingEnter } from "./ui.jsx";

/**
 * 「？」の窓の上半分（サポート基盤 S2a、#150、docs/59 §3）：
 *   この画面でよく見られている質問（その画面の公開の記事を並び順で5件まで。無い画面ではこの段を出さない）
 *   → 押すと窓の中で開く（言葉は t() で会社の言葉）→「解決しましたか［はい］［いいえ］」→ いいえはお問い合わせへ（記事の ID を足あとに）
 *   ヘルプを探す（help-index の題・見出し・本文。公開の記事が1本も無ければ出さない）
 * 記事は /help/help-index.json（FS の Caddy が製品ごとに配る。下書きは入っていない）。開いた・はい・いいえは POST /help/events（人も会社も送らない）
 */

/** help-index を読む（無い・形が違う・製品が違うときは null＝ヘルプの段を出さない） */
export function useHelpIndex(enabled = true) {
  return useQuery({
    queryKey: ["help-index", currentProduct()],
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
    queryFn: async (): Promise<HelpIndex | null> => {
      try {
        const r = await fetch(`${HELP_BASE}/${HELP_INDEX_FILE}`, { headers: productHeaders(), credentials: "same-origin" });
        if (!r.ok) return null;
        const p = HelpIndex.safeParse(await r.json());
        return p.success && p.data.product === currentProduct() ? p.data : null;
      } catch {
        return null;
      }
    },
  });
}

function sendEvent(a: HelpIndexArticle, kind: HelpEventKind): void {
  void api("POST", "/help/events", { article: a.id, feature: a.feature, kind }).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// 記事の本文（help-index の md）を描く。記事に書けるのは 段落・番号つき／点の箇条書き・見出し・太字・リンク・写しだけ（生の HTML はビルドが止める）
// ---------------------------------------------------------------------------
type Inline = { kind: "text"; text: string } | { kind: "bold"; text: string } | { kind: "link"; text: string; to: string };
function inlines(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  for (const m of s.matchAll(re)) {
    if (m.index! > last) out.push({ kind: "text", text: s.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ kind: "bold", text: m[1] });
    else out.push({ kind: "link", text: m[2] ?? "", to: m[3] ?? "" });
    last = m.index! + m[0].length;
  }
  if (last < s.length) out.push({ kind: "text", text: s.slice(last) });
  return out;
}
type Block = { kind: "p"; text: string } | { kind: "h"; text: string } | { kind: "img"; alt: string; name: string } | { kind: "ol" | "ul"; items: Block[][] };
export function blocksOf(md: string): Block[] {
  const lines = md.split("\n");
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (!line.trim()) {
      i++;
      continue;
    }
    const img = /^!\[([^\]]*)\]\(shot:([a-z0-9-]+)\)\s*$/.exec(line.trim());
    const head = /^#{2,6}\s+(.+)$/.exec(line);
    const li = /^(\d+\.|[-*+])\s+(.*)$/.exec(line);
    if (img) {
      out.push({ kind: "img", alt: img[1] ?? "", name: img[2] ?? "" });
      i++;
    } else if (head) {
      out.push({ kind: "h", text: head[1] ?? "" });
      i++;
    } else if (li) {
      const ordered = /^\d/.test(li[1] ?? "");
      const items: Block[][] = [];
      while (i < lines.length) {
        // 手と手のあいだの空の行は飛ばす（同じ種類の箇条書きが続くあいだは1つの箇条書き）
        let j = i;
        while (j < lines.length && !(lines[j] ?? "").trim()) j++;
        const m = /^(\d+\.|[-*+])\s+(.*)$/.exec(lines[j] ?? "");
        if (!m || /^\d/.test(m[1] ?? "") !== ordered) break;
        i = j;
        const body = [m[2] ?? ""];
        i++;
        while (i < lines.length && (!(lines[i] ?? "").trim() || /^\s{2,}/.test(lines[i] ?? ""))) {
          // 空の行のあとに字下げの無い行が来たら、この手はおしまい
          if (!(lines[i] ?? "").trim() && !/^\s{2,}\S/.test(lines[i + 1] ?? "")) break;
          body.push((lines[i] ?? "").replace(/^\s{2,4}/, ""));
          i++;
        }
        items.push(blocksOf(body.join("\n")));
      }
      out.push({ kind: ordered ? "ol" : "ul", items });
    } else {
      const para = [line.trim()];
      i++;
      while (i < lines.length && (lines[i] ?? "").trim() && !/^(#{2,6}\s|\d+\.\s|[-*+]\s|!\[)/.test(lines[i] ?? "")) para.push((lines[i] ?? "").trim());
      out.push({ kind: "p", text: para.join("") });
    }
  }
  return out;
}

type Zoom = (s: HelpShot) => void;
/** 写し（窓の幅に縮めて見せる。押すと原寸の PC の写し） */
function Shot({ s, onZoom }: { s: HelpShot | undefined; onZoom: Zoom }) {
  if (!s) return null;
  return (
    <figure className="hlpshot">
      <button type="button" className="hlpzoom" onClick={() => onZoom(s)} aria-label={`${t(s.alt)}を拡大`}>
        <picture>
          {s.phone ? <source media="(max-width: 640px)" srcSet={s.phone} /> : null}
          <img src={s.pc} alt={t(s.alt)} width={s.w} height={s.h} loading="lazy" decoding="async" />
        </picture>
      </button>
    </figure>
  );
}

function Text({ s, onOpen }: { s: string; onOpen: (id: string) => void }) {
  return (
    <>
      {inlines(s).map((x, i) =>
        x.kind === "bold" ? (
          <b key={i}>{t(x.text)}</b>
        ) : x.kind === "link" ? (
          x.to.startsWith("help:") ? (
            <button key={i} type="button" className="hlplink" onClick={() => onOpen(x.to.slice(5))}>
              {t(x.text)}
            </button>
          ) : (
            <a key={i} href={x.to} target="_blank" rel="noopener">
              {t(x.text)}
            </a>
          )
        ) : (
          <Fragment key={i}>{t(x.text)}</Fragment>
        ),
      )}
    </>
  );
}

function Blocks({ blocks, a, onOpen, onZoom }: { blocks: Block[]; a: HelpIndexArticle; onOpen: (id: string) => void; onZoom: Zoom }): ReactNode {
  return blocks.map((b, i) => {
    if (b.kind === "p")
      return (
        <p key={i}>
          <Text s={b.text} onOpen={onOpen} />
        </p>
      );
    if (b.kind === "h") return <h5 key={i}>{t(b.text)}</h5>;
    if (b.kind === "img") return <Shot key={i} s={a.shots[b.name]} onZoom={onZoom} />;
    const L = b.kind === "ol" ? "ol" : "ul";
    return (
      <L key={i}>
        {b.items.map((item, j) => (
          <li key={j}>
            <Blocks blocks={item} a={a} onOpen={onOpen} onZoom={onZoom} />
          </li>
        ))}
      </L>
    );
  });
}

/** 窓の中で開いた記事。開いたら1回数える */
function Article({ a, onBack, onOpen, onNotSolved }: { a: HelpIndexArticle; onBack: () => void; onOpen: (id: string) => void; onNotSolved: (a: HelpIndexArticle) => void }) {
  const [answer, setAnswer] = useState<"yes" | "no" | null>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const viewer = useImageViewer();
  const zoom: Zoom = (s) => viewer.open([{ src: s.pc, name: t(s.alt) }]);
  useEffect(() => {
    sendEvent(a, "open");
    record("help", `開いた：${t(a.title)}（${a.id}）`);
    title.current?.focus();
  }, [a]);
  const answerWith = (v: "yes" | "no") => {
    setAnswer(v);
    sendEvent(a, v);
    record("help", `${v === "yes" ? "解決した" : "解決しなかった"}：${t(a.title)}（${a.id}）`);
    if (v === "no") onNotSolved(a);
  };
  return (
    <article className="hlpart" data-testid="help-article" data-article={a.id}>
      <button type="button" className="btn text hlpback" onClick={onBack} data-testid="help-back">
        ‹ {HELP_MSG.back}
      </button>
      <h4 ref={title} tabIndex={-1}>
        {t(a.title)}
      </h4>
      {a.sections.map((s, i) => (
        <section key={i}>
          {s.heading ? <h5>{t(s.heading)}</h5> : null}
          <Blocks blocks={blocksOf(s.md)} a={a} onOpen={onOpen} onZoom={zoom} />
        </section>
      ))}
      {viewer.node}
      <p className="hlpfull">
        <a href={a.url} target="_blank" rel="noopener" data-testid="help-full">
          {HELP_MSG.openFull}
        </a>
      </p>
      <div className="hlpsolved" data-testid="help-solved">
        {answer === "yes" ? (
          <p role="status">{HELP_MSG.thanks}</p>
        ) : answer === "no" ? (
          <p role="status">{HELP_MSG.notSolved(t(a.title))}</p>
        ) : (
          <>
            <span>{HELP_MSG.solved}</span>
            <button type="button" className="btn" onClick={() => answerWith("yes")} data-testid="help-yes">
              {HELP_MSG.yes}
            </button>
            <button type="button" className="btn" onClick={() => answerWith("no")} data-testid="help-no">
              {HELP_MSG.no}
            </button>
          </>
        )}
      </div>
    </article>
  );
}

/** 窓の上半分。index が無い・公開の記事が1本も無いときは何も出さない */
export function HelpPanel({ index, screen, onNotSolved, initialArticle }: { index: HelpIndex | null | undefined; screen: string; onNotSolved: (a: HelpIndexArticle) => void; /** 窓を開いたときに開く記事（#206：「お知らせ」の「ヘルプで見る」） */ initialArticle?: string | null | undefined }) {
  const [openId, setOpenId] = useState<string | null>(initialArticle ?? null);
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState<string | null>(null);
  const qid = useId();
  const box = useRef<HTMLDivElement>(null);
  // 窓を開いたら、よく見られている質問の1つ目（無ければ探す欄）に（窓の中に入ったことが分かるように）
  useEffect(() => {
    box.current?.querySelector<HTMLElement>("[data-testid=help-top] button, input")?.focus({ preventScroll: true });
  }, []);
  const articles = (index?.articles ?? []).filter((a) => a.status === "published");
  if (articles.length === 0) return null;
  const top = helpArticlesFor(articles, screen);
  const open = openId ? articles.find((a) => a.id === openId) ?? null : null;
  if (open) return <Article a={open} onBack={() => setOpenId(null)} onOpen={setOpenId} onNotSolved={onNotSolved} />;
  const hits = searched ? helpSearch(articles, searched, t) : [];
  const list = (items: HelpIndexArticle[], testId: string) => (
    <ul className="hlplist" data-testid={testId}>
      {items.map((a) => (
        <li key={a.id}>
          <button type="button" className="hlpitem" onClick={() => setOpenId(a.id)} data-article={a.id}>
            {t(a.title)}
          </button>
        </li>
      ))}
    </ul>
  );
  return (
    <div className="hlp" data-testid="help-panel" ref={box}>
      {top.length ? (
        <section aria-labelledby={`${qid}-top`}>
          <h4 id={`${qid}-top`}>{HELP_MSG.top}</h4>
          {list(top, "help-top")}
        </section>
      ) : null}
      <form
        role="search"
        className="hlpsearch"
        onSubmit={(e) => {
          e.preventDefault();
          setSearched(q.trim() || null);
        }}
      >
        <Field id={qid} label={HELP_MSG.search}>
          <input id={qid} className="inp" type="search" maxLength={HELP_LIMITS.queryMax} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={guardComposingEnter} data-testid="help-q" />
        </Field>
        <button type="submit" className="btn" data-testid="help-search">
          {HELP_MSG.searchButton}
        </button>
      </form>
      {searched ? hits.length ? list(hits.map((h) => h.article), "help-hits") : <p className="hint" data-testid="help-none">{HELP_MSG.searchNone}</p> : null}
    </div>
  );
}
